import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Drawer, Empty, Space, Spin, Typography } from "antd";
import {
    CheckOutlined,
    CloseOutlined,
    ExportOutlined,
} from "@ant-design/icons";
import DocumentPreviewDialog from "../../documents/components/DocumentPreviewDialog";
import TaskService from "../../tasks/services/TaskService";
import {
    PackagePreviewModel,
    TaskActivity,
    TaskPreviewModel,
} from "../../tasks/types";
import { WbTask } from "../../workbench/types/WbTypes";
import {
    formatDateTime,
    formatFileSize,
} from "../utils/workbenchNewSubjectView";

type Props = {
    onClose: () => void;
    task: WbTask | null;
};

type DetailItem = {
    label: string;
    value: React.ReactNode;
};

const unavailable = (
    <Typography.Text type="secondary">Unavailable</Typography.Text>
);

const richTextTags = new Set([
    "a",
    "b",
    "blockquote",
    "br",
    "div",
    "em",
    "i",
    "li",
    "ol",
    "p",
    "s",
    "span",
    "strong",
    "u",
    "ul",
]);

const discardedRichTextTags = new Set([
    "iframe",
    "math",
    "object",
    "script",
    "style",
    "svg",
]);

const richTextAlignment = new Set([
    "center",
    "justify",
    "left",
    "right",
    "start",
    "end",
]);

const getRichTextStyle = (element: HTMLElement): React.CSSProperties => {
    const style: React.CSSProperties = {};
    const alignment = element.style.textAlign || element.getAttribute("align");
    if (alignment && richTextAlignment.has(alignment.toLowerCase())) {
        style.textAlign =
            alignment.toLowerCase() as React.CSSProperties["textAlign"];
    }

    const fontSize = element.style.fontSize;
    if (
        /^\d+(?:\.\d+)?(?:px|pt|em|rem|%)$/i.test(fontSize) ||
        /^(?:xx-small|x-small|small|medium|large|x-large|xx-large|smaller|larger)$/i.test(
            fontSize,
        )
    ) {
        style.fontSize = fontSize;
    }

    return style;
};

const renderRichTextNode = (node: ChildNode, key: number): React.ReactNode => {
    if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
        return null;
    }

    const element = node as HTMLElement;
    const tag = element.tagName.toLowerCase();
    if (discardedRichTextTags.has(tag)) {
        return null;
    }

    const children = Array.from(element.childNodes).map((child, index) =>
        renderRichTextNode(child, index),
    );
    if (!richTextTags.has(tag)) {
        return <React.Fragment key={key}>{children}</React.Fragment>;
    }

    const style = getRichTextStyle(element);

    if (tag === "a") {
        const href = element.getAttribute("href") ?? "";
        const safeHref = /^(https?:\/\/|mailto:|\/(?!\/)|#)/i.test(href);
        return safeHref ? (
            <a
                href={href}
                key={key}
                rel="noopener noreferrer"
                style={style}
                target="_blank"
            >
                {children}
            </a>
        ) : (
            <React.Fragment key={key}>{children}</React.Fragment>
        );
    }

    return React.createElement(tag, { key, style }, children);
};

const renderRichText = (value: string): React.ReactNode => {
    const document = new DOMParser().parseFromString(value, "text/html");
    return Array.from(document.body.childNodes).map((node, index) =>
        renderRichTextNode(node, index),
    );
};

const activityHasRichText = (activity: TaskActivity): boolean =>
    activity.activityType === "CommentUpdate" ||
    activity.activityType === "CommentDelete" ||
    (activity.activityType === "FieldUpdate" &&
        activity.message.startsWith("updated Description"));

const metadataLabelMap: Record<string, string> = {
    amount: "Amount",
    broker: "Broker",
    businessId: "Business ID",
    cedent: "Cedent",
    claimId: "Claim ID",
    claimName: "Claim name",
    claimReference: "Claim reference",
    confidence: "Confidence",
    contractReference: "Contract reference",
    contractType: "Contract type",
    currency: "Currency",
    enrichmentStatus: "Enrichment status",
    entityType: "Entity type",
    insured: "Insured",
    insuredPeriod: "Insured period",
    isHeadlineLoss: "Headline loss",
    lossDate: "Loss date",
    mainClass: "Main class",
    participation: "Participation",
    sourceSystem: "Source system",
    title: "Contract title",
    type: "Type",
    ucr: "UCR",
    umr: "UMR",
    uwYear: "Underwriting year",
};

const taskMetadataOrder = [
    "businessId",
    "contractReference",
    "claimReference",
    "ucr",
    "title",
    "claimName",
    "uwYear",
    "lossDate",
    "cedent",
    "broker",
    "insured",
    "umr",
    "contractType",
    "participation",
    "mainClass",
    "currency",
    "amount",
    "isHeadlineLoss",
    "entityType",
    "type",
    "confidence",
    "enrichmentStatus",
];

const formatTaskReference = (task: WbTask) => {
    if (!task.alphaId) {
        return task.id;
    }

    return typeof task.alphaId === "string"
        ? task.alphaId
        : `${task.alphaId.prefix}-${task.alphaId.index}`;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    !!value && typeof value === "object" && !Array.isArray(value);

const isDateLikeKey = (key: string) =>
    key.toLowerCase().includes("date") || key.toLowerCase().endsWith("at");

const formatMetadataValue = (key: string, value: unknown): React.ReactNode => {
    if (value === null || value === undefined || value === "") {
        return null;
    }

    if (typeof value === "boolean") {
        return value ? "Yes" : "No";
    }

    if (typeof value === "number") {
        return key === "amount" ? value.toLocaleString() : value;
    }

    if (typeof value === "string") {
        return isDateLikeKey(key) ? formatDateTime(value) : value;
    }

    if (value instanceof Date) {
        return formatDateTime(value.toISOString());
    }

    if (Array.isArray(value)) {
        const values = value
            .map((entry) => formatMetadataValue(key, entry))
            .filter(Boolean);
        return values.length ? values.join(", ") : null;
    }

    return null;
};

const addMetadataItem = (
    items: DetailItem[],
    source: Record<string, unknown> | undefined,
    key: string,
) => {
    if (!source || !(key in source)) {
        return;
    }

    const value = formatMetadataValue(key, source[key]);
    if (value !== null && value !== undefined && value !== "") {
        items.push({
            label: metadataLabelMap[key] ?? key,
            value,
        });
    }
};

const buildTaskMetadataItems = (
    taskDetails: TaskPreviewModel | null,
): DetailItem[] => {
    const metadata = taskDetails?.metadata;
    if (!metadata) {
        return [];
    }

    const contractMetadata = metadata.domain?.contracts as
        | Record<string, unknown>
        | undefined;
    const claimMetadata = metadata.domain?.claims as
        | Record<string, unknown>
        | undefined;
    const coreMetadata = metadata.core as Record<string, unknown> | undefined;
    const combined = {
        ...(coreMetadata ?? {}),
        ...(contractMetadata ?? {}),
        ...(claimMetadata ?? {}),
    };
    const items: DetailItem[] = [];

    taskMetadataOrder.forEach((key) => addMetadataItem(items, combined, key));

    if (metadata.project && isRecord(metadata.project)) {
        Object.entries(metadata.project).forEach(([key, value]) => {
            const formatted = formatMetadataValue(key, value);
            if (
                formatted !== null &&
                formatted !== undefined &&
                formatted !== ""
            ) {
                items.push({
                    label: metadataLabelMap[key] ?? key,
                    value: formatted,
                });
            }
        });
    }

    return items.slice(0, 12);
};

const WorkbenchNewTaskDrawer: React.FC<Props> = ({ onClose, task }) => {
    const taskService = useMemo(() => new TaskService(), []);
    const closeButton = useRef<React.ElementRef<typeof Button>>(null);
    const [taskDetails, setTaskDetails] = useState<TaskPreviewModel | null>(
        null,
    );
    const [attachments, setAttachments] = useState<PackagePreviewModel[]>([]);
    const [activities, setActivities] = useState<TaskActivity[]>([]);
    const [previewAttachment, setPreviewAttachment] =
        useState<PackagePreviewModel | null>(null);
    const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let isCurrent = true;

        setTaskDetails(null);
        setAttachments([]);
        setActivities([]);
        setPreviewAttachment(null);
        setIsDescriptionExpanded(false);
        setError(null);

        if (!task) {
            setIsLoading(false);
            return () => {
                isCurrent = false;
            };
        }

        const loadTaskPreview = async () => {
            setIsLoading(true);

            const [taskResponse, activityResponse] = await Promise.all([
                taskService.getTaskById(task.id, null),
                taskService.getTaskActivities(task.id),
            ]);

            if (!isCurrent) {
                return;
            }

            if (taskResponse.isErr()) {
                setError("Task details could not be loaded.");
                setActivities(activityResponse);
                setIsLoading(false);
                return;
            }

            const details = taskResponse.value;
            setTaskDetails(details);
            setActivities(activityResponse);

            if (!details.packageSetId) {
                setIsLoading(false);
                return;
            }

            const packageSetResponse = await taskService.getPackageSet(
                details.packageSetId,
            );

            if (!isCurrent) {
                return;
            }

            if (packageSetResponse.isOk()) {
                setAttachments(packageSetResponse.value.packages ?? []);
            }

            setIsLoading(false);
        };

        void loadTaskPreview();

        return () => {
            isCurrent = false;
        };
    }, [task, taskService]);

    const taskMetadataItems = useMemo(
        () => buildTaskMetadataItems(taskDetails),
        [taskDetails],
    );
    const lastUpdated =
        taskDetails?.updateDate ??
        task?.updatedAt ??
        taskDetails?.createDate ??
        task?.createdAt;
    const status = taskDetails?.statusName || task?.status;
    const taskReference = task ? formatTaskReference(task) : "";
    const taskTitle = taskDetails?.name || task?.title || "Task";

    useEffect(() => {
        if (!task) {
            return undefined;
        }

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape" && !previewAttachment) {
                event.preventDefault();
                onClose();
            }
        };

        document.addEventListener("keydown", handleEscape);
        return () => document.removeEventListener("keydown", handleEscape);
    }, [onClose, previewAttachment, task]);

    return (
        <>
            <Drawer
                autoFocus={true}
                afterOpenChange={(visible) => {
                    if (visible) closeButton.current?.focus();
                }}
                className="workbench-task-drawer"
                closable={false}
                destroyOnClose={true}
                keyboard={true}
                mask={false}
                onClose={onClose}
                open={!!task}
                placement="right"
                title={
                    <div className="workbench-task-drawer-heading">
                        <span
                            aria-hidden={true}
                            className="workbench-task-drawer-icon"
                        >
                            <CheckOutlined />
                        </span>
                        <div className="workbench-task-drawer-title-copy">
                            <Typography.Title
                                className="workbench-task-drawer-title"
                                level={2}
                            >
                                {taskReference
                                    ? `${taskReference} · ${taskTitle}`
                                    : taskTitle}
                            </Typography.Title>
                            <div className="workbench-task-drawer-subtitle">
                                Read-only preview · Workbench remains open
                            </div>
                        </div>
                    </div>
                }
                extra={
                    <Space size={8}>
                        {task ? (
                            <Button
                                href={`/tasks/${encodeURIComponent(task.id)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                icon={<ExportOutlined />}
                            >
                                Open in Tasks
                            </Button>
                        ) : null}
                        <Button
                            aria-label="Close task preview"
                            ref={closeButton}
                            icon={<CloseOutlined />}
                            onClick={onClose}
                        />
                    </Space>
                }
                width={640}
            >
                <Spin spinning={isLoading}>
                    <div className="workbench-task-drawer-scroll">
                        {error ? (
                            <Alert
                                className="workbench-task-drawer-alert"
                                message={error}
                                showIcon={true}
                                type="warning"
                            />
                        ) : null}

                        <section
                            aria-label="Task summary"
                            className="workbench-task-drawer-facts"
                        >
                            <div className="workbench-task-drawer-fact">
                                <span>Status</span>
                                <strong>{status || unavailable}</strong>
                            </div>
                            <div className="workbench-task-drawer-fact">
                                <span>Assignee</span>
                                <strong>
                                    {task?.assigneeName || unavailable}
                                </strong>
                            </div>
                            <div className="workbench-task-drawer-fact">
                                <span>Project</span>
                                <strong>
                                    {task?.projectName || unavailable}
                                </strong>
                            </div>
                            <div className="workbench-task-drawer-fact">
                                <span>Updated</span>
                                <strong>
                                    {lastUpdated
                                        ? formatDateTime(lastUpdated)
                                        : unavailable}
                                </strong>
                            </div>
                        </section>

                        <section className="workbench-task-drawer-section">
                            <h3>Description</h3>
                            {taskDetails?.description ? (
                                <>
                                    <div
                                        className={`workbench-task-drawer-description${
                                            isDescriptionExpanded
                                                ? ""
                                                : " collapsed"
                                        }`}
                                    >
                                        {renderRichText(
                                            taskDetails.description,
                                        )}
                                    </div>
                                    <Button
                                        className="workbench-task-drawer-text-button"
                                        onClick={() =>
                                            setIsDescriptionExpanded(
                                                (expanded) => !expanded,
                                            )
                                        }
                                        type="link"
                                    >
                                        {isDescriptionExpanded
                                            ? "Show less"
                                            : "Show more"}
                                    </Button>
                                </>
                            ) : (
                                unavailable
                            )}
                        </section>

                        <section className="workbench-task-drawer-section">
                            <h3>Key metadata</h3>
                            {taskMetadataItems.length ? (
                                <dl className="workbench-task-drawer-metadata">
                                    {taskMetadataItems.map((item) => (
                                        <div key={item.label}>
                                            <dt>{item.label}</dt>
                                            <dd>{item.value}</dd>
                                        </div>
                                    ))}
                                </dl>
                            ) : (
                                unavailable
                            )}
                        </section>

                        <section className="workbench-task-drawer-section">
                            <h3>
                                Attachments{" "}
                                <span className="workbench-task-drawer-secondary">
                                    {attachments.length}
                                </span>
                            </h3>
                            {attachments.length ? (
                                attachments.map((attachment) => (
                                    <div
                                        className="workbench-task-drawer-attachment-row"
                                        key={attachment.id}
                                    >
                                        <div className="workbench-task-drawer-row-main">
                                            <div className="workbench-task-drawer-attachment-name">
                                                {attachment.fileName}
                                            </div>
                                            <div className="workbench-task-drawer-secondary">
                                                {formatFileSize(
                                                    attachment.fileSizeBytes,
                                                )}
                                            </div>
                                        </div>
                                        <Button
                                            className="workbench-task-drawer-preview-button"
                                            onClick={() =>
                                                setPreviewAttachment(attachment)
                                            }
                                            type="link"
                                        >
                                            Preview
                                        </Button>
                                    </div>
                                ))
                            ) : (
                                <Empty
                                    description="No attachments"
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                />
                            )}
                        </section>

                        <section className="workbench-task-drawer-section">
                            <h3>Latest activity</h3>
                            {activities.length
                                ? activities.slice(0, 5).map((activity) => (
                                      <div
                                          className="workbench-task-drawer-activity-row"
                                          key={activity.id}
                                      >
                                          <span className="workbench-task-drawer-activity-dot" />
                                          <div className="workbench-task-drawer-row-main">
                                              <div className="workbench-task-drawer-activity-message">
                                                  {activityHasRichText(activity)
                                                      ? renderRichText(
                                                            activity.message,
                                                        )
                                                      : activity.message}
                                              </div>
                                              <div className="workbench-task-drawer-secondary">
                                                  {activity.doneByActor
                                                      ?.displayName ||
                                                      activity.doneBy}{" "}
                                                  ·{" "}
                                                  {formatDateTime(
                                                      activity.createdTime,
                                                  )}
                                              </div>
                                          </div>
                                      </div>
                                  ))
                                : unavailable}
                        </section>
                    </div>
                </Spin>
            </Drawer>
            <DocumentPreviewDialog
                isVisible={!!previewAttachment}
                onCloseDialog={() => setPreviewAttachment(null)}
                onDownload={(packageId) => {
                    void taskService.handleDownload([packageId]);
                }}
                packageId={previewAttachment?.id}
                packageName={previewAttachment?.fileName}
            />
        </>
    );
};

export default WorkbenchNewTaskDrawer;
